import React, { useCallback, useEffect, useState } from 'react';
import './SubscriptionPage.scss';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { Navigate } from 'react-router-dom';
import {
  accountTypeToText,
  getTrialDays,
  numberFormat,
} from '../../../shared/utils/utils';
import usePermission from '../../../shared/hooks/global/UsePermission';
import { updateCard } from '../../pricing/services/PricingService';
import usePaddle from '../../../shared/hooks/pricing/UsePaddle';
import {
  getNextPayment,
  prepareUpdatePaymentMethod,
} from '../../../shared/services/pricing/PricingService';
import { format } from 'date-fns';
import { useCreatePortalSession } from '../../../shared/api/hooks/billing/useCreatePortalSession';
import { useBillingIssues } from '../../../shared/hooks/billing/UseBillingIssues';
import { MY_ACCOUNT } from '../../../shared/routes/path';
import { NextPaymentResponse } from '../../../shared/models/pricing/NextPaymentResponse';
import { getMonitorTypeIcon } from '../../prompt/preview/components/stats/helpers/StatsHelper';
import { ProgressBar } from 'primereact/progressbar';
import useDataLayer, { IDefaultDataLayer } from '../../../shared/hooks/global/UseDataLayer';
import { getAccount, toggleAccount } from '../../../shared/services/account/AccountService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NOTIFICATIONS_QUERY_KEY } from '../../../shared/api/query/notifications/keys';
import { getCardBrandLogo } from './cardBrandLogos';
import { isLimitReached, usageLevel } from '../../../shared/helpers/usage/usageLevel';


// Paddle card brand (e.g. "american_express") → display label ("American Express"). Returns null
// for unknown/empty so the UI falls back to the generic card icon + last4 with no brand prefix.
const formatCardBrand = (cardType: string | null): string | null => {
  if (!cardType || cardType === 'unknown') {
    return null;
  }
  return cardType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const SubscriptionPage: React.FC = () => {
  const { accountResponse, setAccountResponse } = useAccount();
  const billingAccess = usePermission('BILLING');
  const queryClient = useQueryClient();

  // After the Paddle card-update checkout completes, the backend processes the webhook (which
  // re-reads the card from the latest transaction) asynchronously. Give it a moment, then refresh
  // the account (new card / cleared red highlights) and the billing notifications (bell + the
  // settings badge derived from them).
  const refreshAfterCardChange = useCallback(() => {
    const refresh = () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
      getAccount()
        .then(setAccountResponse)
        .catch((error) => console.error('Account refresh after card change failed:', error));
    };
    // Webhook is async — refresh shortly after, then once more as a backstop.
    setTimeout(refresh, 3000);
    setTimeout(refresh, 8000);
  }, [queryClient, setAccountResponse]);

  const { paddle } = usePaddle(refreshAfterCardChange);
  const { t } = useTranslation();
  const { pushDataLayer } = useDataLayer();
  const [nextPaymentData, setNextPaymentData] = useState<NextPaymentResponse | null>(null);
  const { mutateAsync: createPortalSession } = useCreatePortalSession();
  const billingIssues = useBillingIssues();

  const { mutate: tryToggleAccount, isPending: isToggling } = useMutation({
    mutationFn: async () => {
      await toggleAccount();
      return getAccount();
    },
    onSuccess: (freshAccountData) => {
      if (freshAccountData.isCanceledAccount) {
        sendCancelEvent();
      }
      setAccountResponse(freshAccountData);
    },
    onError: (error) => {
      console.error('Error toggling account:', error);
    },
  });

  const openUpdateCard = async () => {
    if (!paddle || !accountResponse) {
      console.error('Billing loading failed');
      return;
    }

    const transactionId = await prepareUpdatePaymentMethod();
    if (!transactionId) {
      console.error('Wrong transaction id');
      return;
    }

    updateCard(paddle, transactionId);
  };

  const sendCancelEvent = () => {
    if (!accountResponse) {
      console.error('Cancel event failed');
      return;
    }

    pushDataLayer<IDefaultDataLayer>({
      event: 'subscription_canceled',
      email_hashed: accountResponse.email,
      user_id: accountResponse.userId.toString(),
      account_id: accountResponse.accountId.toString(),
    });
  };

  useEffect(() => {
    if (accountResponse?.accountType === 'TRIAL') {
      setNextPaymentData({ monthlyPrice: 0, discount: null });
      return;
    }
    getNextPayment().then((nextPayment) => {
      setNextPaymentData(nextPayment);
    });
  }, []);

  const renderSkeletonTile = (big = false) => (
    <div className={`settings-tile${big ? ' settings-tile--big' : ''}`}>
      <div className="settings-tile__title">
        <span className="subscription-skeleton subscription-skeleton--title" />
      </div>
      <div className="settings-tile__value">
        <span className="subscription-skeleton subscription-skeleton--value" />
      </div>
      {big && <span className="subscription-skeleton subscription-skeleton--bar" />}
    </div>
  );

  const renderLoadingSkeleton = () => (
    // Distinct key from the real content so React swaps (unmount/mount) instead of morphing the
    // skeleton elements into the real ones — otherwise the global `ico--` transition animates a
    // skeleton bar shrinking into an icon and the text appears to slide in. The value must just
    // emerge in place when the skeleton goes away.
    <div className="subscription-page" key="subscription-skeleton">
      <div className="subscription-page__content">
        <div className="subscription-page__content-header">
          <label>{t('currentPlan')}</label>
        </div>
        <div className="subscription-page__content-box">
          {renderSkeletonTile()}
          {renderSkeletonTile()}
        </div>
      </div>
      <div className="subscription-page__content">
        <div className="subscription-page__content-header">
          <label>{t('paymentMethod')}</label>
        </div>
        <div className="subscription-page__content-box">
          {renderSkeletonTile()}
          {renderSkeletonTile()}
        </div>
      </div>
      <div className="subscription-page__content">
        <div className="subscription-page__content-header">
          <label>{t('usage')}</label>
        </div>
        <div className="subscription-page__content-box">
          {renderSkeletonTile(true)}
          {renderSkeletonTile(true)}
          {renderSkeletonTile(true)}
        </div>
      </div>
    </div>
  );

  // Left-aligned usage tile: title, "X of Y", a tight progress bar, then a % line coloured to
  // match the bar (a red "limit reached" message replaces the % at 100%+).
  const renderUsageTile = (titleKey: string, used: number, max: number) => {
    const percent = max > 0 ? (used / max) * 100 : 0;
    const reached = isLimitReached(used, max);
    const level = usageLevel(used, max);
    return (
      <div className="settings-tile settings-tile--big settings-tile--usage">
        <div className="settings-tile__title">{t(titleKey)}</div>
        <div className={`settings-tile__value${reached ? ' settings-tile__value--error' : ''}`}>
          {`${numberFormat().format(used)} ${t('of')} ${numberFormat().format(max)}`}
        </div>
        <ProgressBar
          className={`progress-bar progress-bar--mini progress-bar--animated progress-bar--${level}`}
          value={percent}
        />
        <div className={`settings-tile__usage-info settings-tile__usage-info--${level}`}>
          {reached ? t('limitReached') : `${Math.round(percent)}%`}
        </div>
      </div>
    );
  };

  // Redirect only when the account IS loaded and the user lacks permission. When it's momentarily
  // null (e.g. the context refetches after a Paddle card-update checkout), fall through and wait —
  // an admin/owner must stay on Subscription, not get bounced to My account.
  if (!isToggling && accountResponse && !billingAccess.read) {
    return <Navigate to={MY_ACCOUNT} replace />;
  }

  // Until the account AND the next-payment amount are both loaded, show skeletons in place of
  // every field.
  if (!accountResponse || nextPaymentData === null) {
    return renderLoadingSkeleton();
  }

  const isPaypal = accountResponse.paymentMethod === 'Paypal';
  const cardBrand = formatCardBrand(accountResponse.cardType);
  const cardBrandLogo = getCardBrandLogo(accountResponse.cardType);

  return (
    <div className="subscription-page" key="subscription-content">
      {accountResponse.paymentSuspended && (
        <div className="subscription-page__payment-suspended-info">
          {t('paymentSuspendedSubscriptionInfo')}
        </div>
      )}

      <div className="subscription-page__content">
        <div className="subscription-page__content-header">
          <label>{t('currentPlan')}</label>
          <div className="subscription-page__content-header-actions">
            {accountResponse.customerId && (
              <Button
                className={`btn btn--sm  btn--primary`}
                onClick={async () => {
                  try {
                    const subscriptionIds = accountResponse.subscriptionId
                      ? [accountResponse.subscriptionId]
                      : [];
                    const { url } = await createPortalSession(subscriptionIds);
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } catch (error) {
                    console.error('Failed to create portal session:', error);
                  }
                }}
              >
                <span className="ico--external-link ico--xs" />
                {t('manageSubscription')}
              </Button>
            )}
            {!accountResponse.subscriptionId &&
              !accountResponse.customerId &&
              accountResponse.isCanceledAccount && (
                <Button className="btn btn--sm btn--primary" onClick={() => tryToggleAccount()}>
                  <span className="ico--refresh ico--xs" />
                  {t('renewAccount')}
                </Button>
              )}
          </div>
        </div>
        <div className="subscription-page__content-box">
          <div className="settings-tile settings-tile--cost">
            {accountResponse.accountType !== 'TRIAL' &&
            !accountResponse.isCanceledAccount &&
            nextPaymentData?.monthlyPrice ? (
              <>
                <div className="settings-tile__title">
                  {t(
                    accountResponse.billingInterval === 'year'
                      ? 'nextAnnuallyPaymentCost'
                      : 'nextMonthlyPaymentCost'
                  )}
                </div>
                <div className="settings-tile__value">
                  ${numberFormat().format(nextPaymentData?.monthlyPrice / 100)}
                  <span className="settings-tile__value-note">{t('exclVAT')}</span>
                </div>
                <div className="settings-tile__tag">
                  {accountTypeToText(accountResponse.accountType)}
                </div>
              </>
            ) : (
              <>
                <div className="settings-tile__title">{t('cost')}</div>
                <div className="settings-tile__value">{t('free')}</div>
                <div className="settings-tile__tag">
                  {accountTypeToText(accountResponse.accountType)}
                </div>
              </>
            )}
          </div>

          <div className="settings-tile">
            {accountResponse.accountType !== 'TRIAL' &&
            ['PAID', 'TRIAL', 'OVERDUE'].includes(accountResponse.accountStatus) ? (
              <>
                <div className="settings-tile__title">
                  {accountResponse.isCanceledAccount ? t('cancelationDate') : t('nextPayment')}:
                </div>
                <div
                  className={`settings-tile__value${
                    billingIssues.paymentDue ? ' settings-tile__value--error' : ''
                  }`}
                >
                  <span className="ico--calendar ico--sm" />
                  {format(accountResponse.endSubscription, 'yyyy-MM-dd ')}
                </div>
              </>
            ) : (
              <>
                <div className="settings-tile__title">{t('trialDays')}:</div>
                <div className="settings-tile__value">{getTrialDays(accountResponse)}</div>
              </>
            )}
          </div>

          {accountResponse.accountType !== 'TRIAL' &&
            !accountResponse.isCanceledAccount &&
            nextPaymentData?.discount &&
            nextPaymentData?.discount.status === 'active' && (
              <div className="settings-tile">
                <div className="settings-tile__title">{t('discount')}</div>
                <div className="settings-tile__value">
                  {nextPaymentData?.discount?.type === 'percentage'
                    ? `${nextPaymentData?.discount?.amount}%`
                    : `$${Number(nextPaymentData?.discount?.amount) / 100}`}
                </div>
              </div>
            )}
        </div>
      </div>

      {accountResponse.paymentMethod && accountResponse.hasSubscription && (
        <div className="subscription-page__content">
          <div className="subscription-page__content-header">
            <label>{t('paymentMethod')}</label>
            <div className="subscription-page__content-header-actions">
              <Button className="btn btn--sm btn--secondary" onClick={() => openUpdateCard()}>
                <span className="ico--card ico--xs" />
                {t('changePaymentMethod')}
              </Button>
            </div>
          </div>
          <div className="subscription-page__content-box">
            <div className="settings-tile">
              <div className="settings-tile__title">{t('paymentMethod')}</div>
              <div className="settings-tile__value">
                {isPaypal ? (
                  accountResponse.paymentMethod
                ) : (
                  <>
                    {!cardBrandLogo && <span className="ico--card ico--sm" />}
                    {`•••• ${accountResponse.paymentMethod}`}
                    {cardBrandLogo && (
                      <img
                        className="settings-tile__card-brand"
                        src={cardBrandLogo}
                        alt={cardBrand ?? 'card'}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
            {!isPaypal && accountResponse.expiryCardMonth && accountResponse.expiryCardYear && (
              <div className="settings-tile">
                <div className="settings-tile__title">{t('cardExpire')}</div>
                <div
                  className={`settings-tile__value${
                    billingIssues.cardExpiring ? ' settings-tile__value--error' : ''
                  }`}
                >
                  <span className="ico--calendar ico--sm" />
                  {`${accountResponse.expiryCardMonth}/${accountResponse.expiryCardYear}`}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="subscription-page__content">
        <div className="subscription-page__content-header">
          <label>{t('usage')}</label>
        </div>
        <div className="subscription-page__content-box">
          {renderUsageTile(
            'questionLimits',
            accountResponse.createdMonitorCount,
            accountResponse.maxMonitorCount
          )}

          {renderUsageTile('userLimits', accountResponse.usersOnAccount, accountResponse.maxUsers)}

          <div className="settings-tile settings-tile--big settings-tile--usage">
            <div className="settings-tile__title">{t('activeModels')}</div>
            <div className="settings-tile__block">
              {accountResponse.features.map((feature) => (
                <span key={feature} className={`${getMonitorTypeIcon(feature)} ico--md`} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {!accountResponse.subscriptionId && !accountResponse.isCanceledAccount && (
        <div className="subscription-page__delete-account">
          <Button
            className="btn btn--link btn--link-blue"
            label={t('startCancellation')}
            onClick={() => tryToggleAccount()}
          />
          <span className="subscription-page__delete-account-question">
            {t('cancellationProcessSuffix')}
          </span>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
